import { defineStore } from 'pinia';
import {
  DAMAGED_CONDITION,
  GOOD_CONDITION,
  PERFECT_CONDITION,
} from '~/models/ListingProduct.api.model';
import type { HalebopRequestErrorData } from '~/repository/factory';

export type BuybackItem = {
  BuybackItemId: number;
  ModelName: string;
  ManufacturerArticleNumber: string;
  Manufacturer: string;
  Color: string;
  ColorCode: string;
  Storage: string;
  FrontImageUrl: string;
  BackImageUrl: string;
};

export type ConditionsEstimate = {
  AdditionalSubsidizationAmount: number;
  Condition: string;
  DownPaymentTime: number;
  TotalMonthlyCost: number;
  TradeInMonthlyDataAmount: number;
  TradeInMonthlyValue: number;
  TradeInTotalValue: number;
};

export type BuybackData = {
  type: 'toProduct' | 'toOrder' | 'valuate';
  valuateOnly: boolean;
  selectedProductId: number | null;
  selectedManufacturer: string | null;
  selectedPlanId: number | null;
  orderNumber: number | null;
  downPaymentTime?: number | null;
};

type state = {
  data: BuybackData;
  searchResults: BuybackItem[];
  searchDropdownVisible: boolean;
  selectedBuybackProduct?: BuybackItem;
  selectedBuybackName: string;
  step2Active: boolean;
  step3Active: boolean;
  showScrollIndicator: boolean;
  conditionsEstimates?: ConditionsEstimate[];
  selectedCondition: string;
  selectedEstimate?: ConditionsEstimate;
  campaignHero?: string;
};

export const useBuybackStore = defineStore('buyback', {
  state: (): state => ({
    data: {
      type: 'valuate',
      valuateOnly: true,
      selectedProductId: null,
      selectedManufacturer: null,
      selectedPlanId: null,
      orderNumber: null,
      downPaymentTime: null,
    },
    searchResults: [],
    searchDropdownVisible: true,
    selectedBuybackProduct: undefined,
    selectedBuybackName: '',
    step2Active: false,
    step3Active: false,
    showScrollIndicator: false,
    conditionsEstimates: [],
    selectedCondition: '',
    selectedEstimate: undefined,
    campaignHero: undefined,
  }),
  actions: {
    async getBuybackCampaignHero() {
      if (this.data.type !== 'toProduct') {
        return;
      }

      const { $api } = useNuxtApp();
      try {
        const hero = await $api.BuybackModule.getCampaignHero(
          this.data.orderNumber,
        );
        this.campaignHero = hero.CampaignHeroHtml;
      } catch (error) {
        this.campaignHero = undefined;
      }
    },
    async buybackSearch(searchString: string) {
      this.resetExceptSearchFieldInput();
      const { $api } = useNuxtApp();
      const searchResult = await $api.BuybackModule.findBuybackItem(
        10,
        searchString,
      );
      this.searchResults = searchResult.Items;
    },
    resetExceptSearchFieldInput() {
      this.searchResults = [];
      this.selectedBuybackProduct = undefined;
      this.step2Active = false;
      this.step3Active = false;
      this.conditionsEstimates = [];
      this.selectedCondition = '';
      this.selectedEstimate = undefined;
    },
    reset() {
      this.searchResults = [];
      this.selectedBuybackProduct = undefined;
      this.step2Active = false;
      this.step3Active = false;
      this.conditionsEstimates = [];
      this.selectedCondition = '';
      this.selectedBuybackName = '';
      this.selectedEstimate = undefined;
    },
    async selectBuyback(item: BuybackItem) {
      this.searchResults = [];
      this.selectedBuybackProduct = item;
      this.selectedBuybackName = [
        item?.Manufacturer,
        item?.ModelName,
        item?.Storage,
        item?.Color,
      ].join(' ');

      this.step2Active = true;
      this.showScrollIndicator = true;
      const { $api } = useNuxtApp();

      try {
        const conditionEstimates =
          await $api.BuybackModule.getConditionEstimates(
            this.selectedBuybackProduct.BuybackItemId,
            this.data.valuateOnly,
            this.data.orderNumber,
          );
        this.conditionsEstimates = conditionEstimates.Estimations;
      } catch (e) {
        if (isNuxtError<HalebopRequestErrorData>(e)) {
          // todo: Show an error message in buyback drawer
          // If we use the notifcationStore standard message it will show the error in
          // the wrong spot on kvitto pages
        }
      }
    },
    async changeCondition() {
      const { $api } = useNuxtApp();

      if (this.selectedBuybackProduct !== undefined) {
        try {
          const conditionEstimates = await $api.BuybackModule.valuateDevice(
            this.selectedCondition,
            this.selectedBuybackProduct.BuybackItemId,
            this.data.downPaymentTime ?? null,
            this.data.valuateOnly,
            this.data.orderNumber,
          );

          this.selectedEstimate = conditionEstimates.Estimation;
          this.showScrollIndicator = true;
          this.step3Active = true;
        } catch (e) {
          if (isNuxtError<HalebopRequestErrorData>(e)) {
            // todo: Show an error message in buyback drawer
            // If we use the notifcationStore standard message it will show the error in
            // the wrong spot on kvitto pages
          }
        }
      }
    },

    async addPerk(perkType: string) {
      const { $api } = useNuxtApp();
      if (this.selectedBuybackProduct !== undefined) {
        const checkoutStore = useMyCheckoutStore();
        const orderStore = useMyOrderStore();
        const notificationStore = useNotificationStore();

        if (this.data.type === 'toProduct') {
          try {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const addBuybackItemToCart =
              await $api.BuybackModule.addBuybackItemToCart(
                perkType,
                this.selectedCondition,
                this.selectedBuybackProduct?.BuybackItemId,
                this.data.downPaymentTime ?? null,
                this.data.valuateOnly,
              );
            await checkoutStore.fetchCart();
          } catch (e) {
            notificationStore.setStandardErrorNotification();
          }
        } else if (this.data.type === 'toOrder') {
          if (this.data.orderNumber !== null) {
            try {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const addBuybackItemToCart =
                await $api.BuybackModule.addBuybackItemToOrder(
                  perkType,
                  this.selectedCondition,
                  this.selectedBuybackProduct?.BuybackItemId,
                  this.data.orderNumber,
                );

              orderStore.buybackItemWasAdded = true;
            } catch (e) {
              notificationStore.setStandardErrorNotification();
            }
          }
        }
      }
    },
  },
  getters: {
    conditionPerfect(state: state): ConditionsEstimate | null {
      const conditionPerfect = state.conditionsEstimates?.find(
        (f) => f.Condition === PERFECT_CONDITION,
      );
      return conditionPerfect ? conditionPerfect : null;
    },
    conditionGood(state: state): ConditionsEstimate | null {
      const conditionGood = state.conditionsEstimates?.find(
        (f) => f.Condition === GOOD_CONDITION,
      );
      return conditionGood ? conditionGood : null;
    },
    conditionDamaged(state: state): ConditionsEstimate | null {
      const conditionDamaged = state.conditionsEstimates?.find(
        (f) => f.Condition === DAMAGED_CONDITION,
      );
      return conditionDamaged ? conditionDamaged : null;
    },
  },
});
